import React from 'react'
import classNames from 'classnames'
import { useNavContext } from '../useNavContext'
import { MobileChildMenuProps } from '../ContentfulNavigation'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import MobileMenuFooter from './MobileMenuFooter'

// Multi-use component that handles subMenu navigation for main nav categories and the region switcher
// Styling and behavior will be the same, but specific funcionality (menu clicks, etc) and content will be different
// This is effectively just a UI wrapper that handles styling, while content and state management is handled by different parents
const MobileChildMenu = ({
  ariaLabel,
  children,
  handleBackButton,
  handleCloseButton,
  id,
  isOpen,
  showFooterMenu,
  title
}: MobileChildMenuProps) => {
  const { headerHeight, isMobileNavOpen } = useNavContext()
  const windowSize = useWindowSize()

  const isDesktopLayout = windowSize.width >= 1024
  const isMobileLayout = windowSize.width < 768

  const subMenuClasses = classNames(
    'w-screen bg-white h-100vh overflow-y-scroll !no-scrollbar pb-6 fixed top-[0px] md:top-auto z-[100] md:z-[90] transition-right md:transition-left ease-out !duration-500 md:border-r md:border-gray-25 w-full md:max-w-[375px] lg:hidden',
    {
      'invisible right-[-800px] md:left-[-500px]':
        !isOpen || !isMobileNavOpen || isDesktopLayout,
      'right-0 md:left-[375px]': isOpen && isMobileNavOpen && !isDesktopLayout
    }
  )

  return (
    <nav
      aria-hidden={!isOpen}
      aria-label={ariaLabel}
      className={subMenuClasses}
      data-testid='MobileChildMenu'
      id={id}
      style={{
        height:
          windowSize.width >= 768 ? `calc(100vh - ${headerHeight}px)` : '100vh',
        marginBottom: 'env(safe-area-inset-bottom)',
        top: windowSize.width >= 768 ? `${headerHeight}px` : '0'
      }}
    >
      <div className='flex md:hidden flex-nowrap justify-between items-center border-b border-gray-25 px-4 py-3'>
        <button
          aria-label='Return to Previous Menu'
          className='before:content-icon-chevron-left before:h-6 before:w-6 relative top-1'
          onClick={handleBackButton}
        />
        <h4 className='font-effra text-[22px] leading-8'>{title}</h4>
        <button
          aria-label='Close Mobile Navigation'
          className='w-5 h-5'
          onClick={handleCloseButton}
        >
          <span
            aria-hidden='true'
            className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out rotate-45'
          />
          <span
            aria-hidden='true'
            className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out opacity-0'
          />
          <span
            aria-hidden='true'
            className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out -rotate-45'
          />
        </button>
      </div>
      <div className='px-4 py-6'>{children}</div>
      {showFooterMenu && isMobileLayout ? (
        <div className='px-4'>
          <MobileMenuFooter />
        </div>
      ) : null}
    </nav>
  )
}

export default MobileChildMenu
